// Cliente del BFF Ona Pulse (contrato en docs/04 + ficha docs/pulse-bff.md del
// repo hotelos). MODE 'auto': intenta el BFF real y, si no responde, cae al
// demo POR ENDPOINT — la app siempre abre, y enseña qué es real y qué es demo.

import { ALERTS, G, HOTELS, IA_ANSWERS, type Alerta } from '@/constants/data';
import { Platform } from 'react-native';
import { sesionGuardada } from '@/lib/auth';

export type Mode = 'auto' | 'mock' | 'real';
export type Source = 'live' | 'demo' | 'denegado';

/* Versión de la APP, visible en el pie de Pulse. Va en el código (no en el
   manifest) a propósito: el bundle nuevo SIEMPRE la trae — es la prueba de
   «¿se actualizó?». Bumpearla junto con app.json en cada entrega. */
/* 🔴 DOS NÚMEROS QUE NO SON EL MISMO, Y CONVIENE SABERLO:
     · `APP_VERSION` (aquí)      → la versión de VERDAD, la que se ve en el pie
                                    de la portada. Sube en cada entrega.
     · `version` de app.json     → la del BINARIO (lo que enseña Android en
                                    Ajustes). Se queda FIJA en 0.3.24 a
                                    propósito: comprobado que tocarla cambia la
                                    huella del binario, y con huella nueva las
                                    actualizaciones por aire dejan de llegar a
                                    los móviles ya instalados. Solo se sube
                                    cuando de todas formas toca binario nuevo. */
export const APP_VERSION = '0.5.5';

export const MODE: Mode = 'auto';
// Servidor INDEPENDIENTE ona-pulse-api (proceso propio, puerto 4090), publicado
// por nginx como /pulse-api/ bajo el dominio del DEV de Hotelos — que se llama
// hotelos-v3 (🔴 no «hotelos-dev»). Decisión 2026-08-22: Pulse no comparte
// proceso ni código con Hotelos — solo lee su base de datos.
/* En la versión web servida desde nuestro dominio, el API se llama por el MISMO
   origen (`/pulse-api`, que nginx redirige al proceso :4090). Así el navegador
   no lo trata como petición entre dominios y no hace falta abrir CORS en el
   servidor — menos superficie y menos configuración que mantener. */
const MISMO_ORIGEN = Platform.OS === 'web'
  && typeof window !== 'undefined'
  && !/^https?:\/\/(localhost|127\.0\.0\.1|192\.168\.)/.test(window.location?.origin ?? '');
/* 🔴 A QUÉ ENTORNO APUNTA LA APP INSTALADA. La web se llama a sí misma y no
   tiene duda; el instalador de Android, en cambio, lleva la dirección DENTRO,
   y hasta ahora esa dirección era la de DESARROLLO. Un APK repartido a
   dirección con datos de dev sería el mismo error de los 227 € de Diana Park,
   pero en manos de todos. Ahora se decide al construir:
     EXPO_PUBLIC_PULSE_API=https://pulse.onasystem.net/pulse-api  → producción
   y si no se dice nada, sigue siendo dev (que es lo que se quiere en el día a
   día). El pie de la portada enseña la versión del API, así que la confusión
   se ve desde la propia pantalla. */
const API_PROD = 'https://pulse.onasystem.net/pulse-api';
const API_DEV = 'https://hotelos-v3.onasystem.net/pulse-api';
const API_ENV = process.env.EXPO_PUBLIC_PULSE_API;
/* 🔴 SI NO SE DICE NADA, ¿A DÓNDE VA? Antes: a desarrollo. Y estaba al revés,
   como se vio el 24-08: una actualización por aire publicada sin la variable
   —`eas update` no hereda el `env` del perfil, y encima Metro reusaba el bundle
   cacheado— reapuntó el APK de producción, ya repartido, a los datos de dev.
   Nadie recibe un aviso de eso; simplemente ve cifras que no son.
   Ahora lo decide `__DEV__`, que el empaquetador fija solo y no se puede
   olvidar: un bundle de PUBLICACIÓN (binario o actualización por aire) va a
   producción salvo que se le diga otra cosa, y `expo start` en el Mac sigue
   yendo a desarrollo. La variable se respeta cuando está —sirve para apuntar un
   APK interno a dev— pero ya no es lo que sostiene el caso importante. */
export const BASE_URL = MISMO_ORIGEN ? '/pulse-api'
  : (API_ENV || (__DEV__ ? API_DEV : API_PROD));
export const ES_PRODUCCION = MISMO_ORIGEN || BASE_URL === API_PROD;
/* Nombre del servidor al que habla la app, para poder ENSEÑARLO cuando algo
   falla. Un mensaje de error que no dice contra qué preguntó manda a buscar el
   fallo al sitio equivocado (pasó el 24-08). */
export const SERVIDOR = BASE_URL.replace(/^https?:\/\//, '').replace(/\/.*$/, '') || 'este dominio';
/* Token DEV de solo lectura, TEMPORAL hasta que todos los móviles usen Entra.
   🔴 NO SE ESCRIBE AQUÍ, Y ESA ES LA GRACIA. La primera versión lo dejaba como
   respaldo dentro de un `ES_PRODUCCION ? '' : '0082…'`. En producción no se
   usaba —valía cadena vacía— pero el literal SEGUÍA DENTRO del APK: se veía
   descomprimiendo el fichero. Y aunque contra producción no sirve de nada (allí
   PULSE_LEGACY_TOKEN=0), contra DEV sí abre, y DEV lleva los datos reales del
   grupo. Ahora entra por variable de entorno al construir: el binario que se
   reparte no lo contiene porque no existe en ninguna parte del código.
     · desarrollo → mobile/.env (no se sube al repositorio)
     · builds     → env del perfil en eas.json
     · producción → no se pone, y punto */
const PULSE_TOKEN = ES_PRODUCCION ? '' : (process.env.EXPO_PUBLIC_PULSE_TOKEN || '');
const TIMEOUT_MS = 12000;

export interface OutletLive {
  dim: string; nombre: string;
  real: number | null; presupuesto: number | null; anterior: number | null;
  brecha: number | null; publicado: boolean;
  realAnio: number | null; presuAnio: number | null;
}
export interface BrechaParte { causa: string; que: string; imp: number }
export interface ComercialHotel {
  id: number; nombre: string;
  hoy: { importe: number | null; hasta?: string | null; cerrado: boolean; fecha?: string; puntos?: Array<{ punto: string; total: number }> };
  objetivoDia: number | null;
  patron: string;
  mes: null | {
    real: number; conseguido: number; presupuesto: number; objetivoHasta: number | null;
    pctReal: number; pctMes: number; faltan: number | null; diasRestantes: number;
    porDia: number | null; ayer: number | null;
    /* Hasta qué día del mes llega el FOLIO de Noray. Normalmente ayer. `null` = el
       mes acaba de empezar y todavía no hay días cerrados. Se enseña para que el
       lector sepa de cuántos días es el acumulado. */
    hastaDia: number | null;
  };
  palancas: null | { ocReal: number | null; ocBud: number | null; arrReal: number | null; arrBud: number | null };
  anio: null | { real: number; presupuesto: number; pct: number; pctAnio: number };
  outlets: OutletLive[];
  brecha: BrechaParte[];
  sinPresupuesto: boolean;
}
/* `fuente` declara de dónde sale el Real: el mes es CONTABLE (como la Cuenta de
   Explotación) y el año es la foto publicada de Control de Gestión más el mes en
   curso. `mesesSinPublicar` no está de adorno: si falta un mes por publicar, el año
   va CORTO y la pantalla debe poder decirlo en vez de parecer una caída. */
/* Los KPI de la portada de Sistemas. Cada bloque puede venir `null` si su tabla
   falla: la pantalla lo DICE en vez de enseñar un cero, que sería mentira.
   🔴 `estado` y `sev` llegan como texto libre desde Hotelos y NO se traducen aquí:
   inventarse el vocabulario de una columna es el fallo que costó cuatro versiones
   en la Cuenta de Explotación. Se enseñan tal cual hasta que lo confirmemos. */
export interface SistemasKpis {
  cierres: null | {
    total: number | null; ok: number | null; incidencias: number | null;
    errores: number | null; revisado: string | null;
    porEstado: Array<{ estado: string; n: number }>;
    pendientes: Array<{ hotel: string; estado: string | null; vacia: boolean;
                        lineas: number; desde: string | null; error: string | null }>;
  };
  tickets: null | {
    abiertos: number; sinAsignar: number; fueraSla: number; guardia: number;
    masViejo: string | null; sync: string | null;
    sinDueno: Array<{ id: number; asunto: string; desde: string | null;
                      hotel: string | null; tipo: string | null }>;
  };
  incidencias: null | {
    abiertas: number; porSeveridad: Array<{ sev: string; n: number }>;
    resueltasAuto7d: number;
    lista: Array<{ id: number; titulo: string; sev: string | null;
                   servicio: string | null; desde: string | null }>;
  };
  salud: null | {
    servicios: Array<{ servicio: string; nombre: string; estado: string | null;
                       ms: number | null; detalle: string | null; revisado: string | null }>;
    porEstado: Array<{ estado: string; n: number }>;
  };
  saldo: null | {
    saldoUsd: number; creditoUsd: number; gastoUsd: number; estado: string | null;
    error: string | null; revisado: string | null; obsoleto: boolean;
    horasDesde: number; porDia: number | null; diasRestantes: number | null;
  };
}

/* Asset Management. `sinFecha` y `absurdas` NO son detalle técnico: un contrato
   que no se puede leer no debe desaparecer en silencio de una lista de
   vencimientos, así que la pantalla dice cuántos se quedaron fuera. */
export interface AssetKpisLive {
  vencimientos: null | {
    total: number; sinFecha: number; absurdas: number; en24meses: number;
    vencidos: Array<{ nombre: string; hotelId: number | null; tipo: string | null;
                      fin: string; meses: number; renta: string | null; prorrogas: string | null }>;
    proximos: Array<{ nombre: string; hotelId: number | null; tipo: string | null;
                      fin: string; meses: number; renta: string | null; prorrogas: string | null }>;
  };
  calidad: null | {
    unidades: number; sinHotel: number; sinCoordenadas: number;
    hotelesSinCoordenadas: string[];
  };
}

/* 🔴 `hayImportes` viaja a propósito: solo hay PORCENTAJES pactados. Los importes
   de comisión únicamente existen en el espejo de Avalon —un hotel—, así que no se
   pueden dar a nivel grupo, y la tarjeta tiene que decirlo en vez de dejar que
   alguien lo pregunte dentro de tres meses. */
export interface ComisionesLive {
  acuerdos: number; agencias: number; hayImportes: boolean; nota: string;
  lista: Array<{ agencia: string; hoteles: number; min: number | null; max: number | null; tipos: string[] }>;
}

export interface ComercialLive {
  fecha: string; hoteles: ComercialHotel[];
  fuente?: {
    mes: string; anio: string;
    mesesFoto: number[]; mesesSinPublicar: number[]; ms: number;
    /* Meses cerrados que NO estaban publicados y se han recalculado de
       contabilidad. Pasa entre el fin de mes y la publicación en Control de
       Gestión: sin esto el año se quedaría sin el mes que acaba de cerrar. */
    mesesRecalculados?: number[]; ultimoDia?: number;
  };
}

/* Ficha del hotel: dónde está y quién lo dirige. 🔴 El director se DEDUCE de a
   quién está asignado el hotel en Hotelos (`usuario_hoteles_iris`), no de una
   columna «director»: si alguien está mal asignado allí, aquí sale igual — y eso
   es lo correcto, la fuente de verdad es Hotelos. Sin correo ni teléfono. */
/* Un mes contra el mismo mes del año anterior. 🔴 `ventana` no es decoración: si
   el mes está en curso se comparan los MISMOS días en los dos años (1 al 24, por
   ejemplo). Comparar medio agosto contra un agosto entero pinta un desplome del
   23 % que no ha ocurrido. */
export interface ComparaAnioLive {
  hotel: string; id: number;
  mes: number; anio: number; anioAnterior: number; enCurso: boolean;
  ventana: { esteAnio: { desde: string; hasta: string }; anterior: { desde: string; hasta: string }; dias: number };
  total: number; totalAnterior: number;
  delta: number | null; pct: number | null;
  fuentes: { esteAnio: { onasystem: number | null; noray: number | null };
             anterior: { onasystem: number | null; noray: number | null } };
  /** Folios en OnaSystem Y en Noray en la misma ventana: mes de migración, la
      suma puede contar doble. Se dice, no se adivina. */
  solapan: boolean;
  hayAnterior: boolean;
  avisos: string[];
  nota: string;
}

export interface FichaHotelLive {
  id: number; nombre: string;
  direccion: string | null; direccionLibre: string | null;
  cp: string | null; localidad: string | null; provincia: string | null;
  habitaciones: number | null; plazas: number | null;
  zona: string | null; codigoBc: string | null; enCg: boolean | null;
  director: string | null; directores: string[]; regionales: string[];
  /** El «nombre a mostrar» completo del directorio: «Amparo Sequi – Dirección
      Cala Pi». Lleva el cargo dentro, así que se enseña tal cual. */
  mostrar?: string[];
  /** El puesto de Entra, si lo tiene. */
  puesto?: string | null;
  /** Por qué vía se ha sabido, de más a menos fiable: declarado en Premios Noa
      con vigencia · nombre a mostrar de Entra ID · permisos por hotel. No valen
      lo mismo y la respuesta lo dice. */
  via?: string | null;
  /** Fecha a la que se ha resuelto la vigencia (hoy salvo que se pida otra). */
  fecha?: string;
  /** Vigencia del director actual. `hasta` null = sigue en vigor. */
  desde?: string | null; hasta?: string | null;
  /** 🔴 El historial de directores del hotel. Es lo que permite contestar «quién
      era el director en julio» cuando ha habido un relevo — y por eso la fuente
      son vigencias y no un campo en la ficha del hotel. */
  historial?: Array<{
    quien: string; mostrar: string | null;
    desde: string | null; hasta: string | null;
    vigente: boolean; nota: string | null;
  }>;
  /** ¿Está DECLARADO en Premios Noa? Si no, sale de una fuente de respaldo. */
  declarado?: boolean; declaradoLegible?: boolean;
  alcanceLegible: boolean; nota: string;
}

export interface FuentePms {
  pms: string;
  ok: boolean;
  hoteles: number;
  dia?: number | null;
  mes?: number | null;
}

export interface Brief {
  fecha?: string;
  fechaNatural?: string;
  esperandoSync?: boolean;
  prodDia: number;
  prodMes: number;
  budMes: number | null;
  budPct: number | null;
  deltaDia: number | null;
  occ: number | null;
  occPorPms?: Array<{ pms: string; media: number; hoteles: number }>;
  adr?: number | null;
  adrPorPms?: Array<{ pms: string; adr: number; rn: number }>;
  fuentes?: FuentePms[];
  hoteles: number;
  pms: number;
}

export interface HotelRow {
  id?: number;
  n: string;
  pms: string;
  fecha?: string;
  /* Producción del día de referencia. 🔴 `null` NO es cero: significa que ese
     cargo todavía no está en el espejo. Cuando pasa, `dFecha` dice cuál es el
     último día que sí tiene y `dUlt` cuánto fue. Todos los hoteles cierran el
     mismo día, así que un null es un aviso, no una rareza del hotel. */
  d: number | null;
  dFecha?: string | null;
  dUlt?: number | null;
  mes: number;
  occ: number | null;
  occ30?: Array<number | null> | null;
  ob?: number;
  adr: number | null;
  rn?: number | null;
  /** ¿Hace el cierre hotelero mensual (interruptor CDG de Hotelos)? Los 39
      que lo hacen son exactamente los 39 que tienen presupuesto en
      `pl_budget_real`, y son los únicos con los que se puede comparar contra
      budget. 🔴 Es una MARCA, no un filtro: los otros 14 facturan (4,2 M€
      del mes) y TIENEN ocupación, así que siguen viniendo en la lista y van
      agrupados como «Otros Hoteles». Ausente en demo → se trata como que sí. */
  cg?: boolean;
  bud: number | null;
  /** Producción diaria de los últimos 7 días. */
  spark: number[];
  /** Producción ACUMULADA del mes, día a día. Es la curva que se lee como
      «cómo va el mes» — siete barras sueltas no lo cuentan. */
  acum?: number[];
}

/* Una fila del podio TAL COMO LA PUBLICÓ Hotelos. Ya no se recalcula nada aquí:
   el nombre, el valor y las Caracolas vienen de la edición publicada. */
export interface PremioFila {
  id?: number | null;
  n: string;
  puesto: number;
  tipo?: string;             // HOTEL | OUTLET | USUARIO | DEPTO
  valor?: number | null;     // el valor de la métrica del premio
  detalle?: string | null;
  caracolas?: number;
  mio?: boolean;             // uno de los tuyos, si tienes alcance
}

export interface Premio {
  disponible: boolean;
  pendiente?: boolean;       // hay edición esperando a que alguien la publique
  motivo?: string;
  anio?: number | null;
  mes?: number | null;
  premio?: string;           // «La Tortugona del mes»
  codigo?: string;
  ambito?: string;
  metrica?: string;
  publicadoPor?: string | null;
  publicadoAt?: string | null;
  podio?: PremioFila[];
  mios?: Array<{ id: number | null; n: string; puesto: number }> | null;
  nota?: string;
}

export type IaFila = [string, string, 'good' | 'bad' | 'flat'];
/* Un desglose largo (los 13 puntos de venta de un hotel, el árbol de cuentas de
   un día) en una lista plana no hay quien lo lea. Las secciones le dan al chat
   lo mismo que tiene el informe en Hotelos: un título, sus líneas y su
   subtotal. */
export interface IaSeccion {
  t: string;
  sub?: string;
  filas: IaFila[];
  total?: [string, string];
}
export interface IaReply {
  txt: string;
  rows: IaFila[];
  foot: string;
  tortugonas?: boolean;   // el podio se pinta con Tortugonas (oro/plata/bronce), no medallas
  secciones?: IaSeccion[];
  /* Para el cuaderno: qué intención reconoció y si de verdad supo contestar.
     No se enseñan; sirven para saber qué enseñarle después. */
  intencion?: string;
  noSupe?: boolean;
}

export interface Res<T> { data: T; source: Source }

/* «Ahora mismo»: folios vivos del día (espejo de Facturación Noray). LIVE-only:
   sin datos (demo/red caída) devuelven null y Noa lo dice con naturalidad. */
export interface HoyLive {
  fecha: string;
  ultSync: string | null;                 // 'HH:MM' Madrid de la última sync
  total: number;                          // neto sin IVA, provisional
  hotelesConDatos: number;
  hoteles: Array<{ id: number; n: string; imp: number; lineas: number }>;
  puntos: Array<{ cat: string; punto: string; imp: number }>;
  nota: string;
}
export interface PdvLive {
  q: string;
  encontrado: boolean;
  fecha: string;
  ultSync: string | null;
  puntos?: Array<{ punto: string; mes: number }>;
  hoy?: number;
  ayer?: number;
  mes?: number;
  porHotel?: Array<{ id: number; n: string; mes: number }>;
  disponibles?: Array<{ punto: string; mes: number }>;
  nota?: string;
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

/* Cabeceras de identidad. Con sesión de Entra se manda el token de la PERSONA
   (el API verifica su firma y lo cruza con Hotelos); el token compartido queda
   solo como respaldo para los móviles que aún no han iniciado sesión y morirá
   en cuanto todos usen Entra. `sesionGuardada()` renueva sola si hizo falta. */
async function cabeceras(): Promise<Record<string, string>> {
  const h: Record<string, string> = { Accept: 'application/json', 'X-Pulse-Token': PULSE_TOKEN };
  try {
    const s = await sesionGuardada();
    if (s?.idToken) h.Authorization = `Bearer ${s.idToken}`;
  } catch { /* sin sesión: se va con el token heredado */ }
  return h;
}

async function real<T>(path: string, cuerpo?: unknown, reintento = false): Promise<T> {
  // 🔴 Las cabeceras se preparan ANTES de armar el cronómetro: `cabeceras()`
  // puede tardar (lee el almacén seguro y, si el token caducó, lo renueva
  // contra Microsoft). Si el reloj corría ya, ese tiempo se comía el margen de
  // la petición y la llamada nacía abortada — sin código de estado, así que la
  // app lo tomaba por «se cayó la red» y caía al DEMO en vez de decir la verdad.
  const headers = await cabeceras();
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const opciones = cuerpo === undefined
      ? { headers, signal: ctrl.signal }
      : { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(cuerpo), signal: ctrl.signal };
    let res = await fetch(`${BASE_URL}${path}`, opciones);
    /* 🔴 UN 401 MERECE UN SEGUNDO INTENTO. Puede ser simplemente que el token
       acabara de caducar entre que se leyó y que llegó la petición. Sin este
       reintento, ese parpadeo se enseñaba como «SIN ACCESO» para siempre,
       porque nada volvía a pedir el dato. Se reintenta UNA vez con cabeceras
       frescas; si vuelve 401, entonces sí es que no hay acceso. */
    if (res.status === 401 && !reintento) {
      const frescas = await cabeceras();
      if (frescas.Authorization && frescas.Authorization !== headers.Authorization) {
        return real<T>(path, cuerpo, true);
      }
    }
    if (!res.ok) {
      let msg = '';
      try { msg = ((await res.json()) as { error?: string }).error || ''; } catch { /* sin cuerpo */ }
      /* Un 401 que sobrevive al reintento significa que la sesión está muerta:
         se avisa UNA vez (si no, cinco peticiones darían cinco avisos). */
      if (res.status === 401 && !yaAvisado) { yaAvisado = true; avisarPerdida?.(); }
      throw Object.assign(new Error(msg || `BFF ${path} → HTTP ${res.status}`), { status: res.status });
    }
    yaAvisado = false;
    return (await res.json()) as T;
  } finally {
    clearTimeout(t);
  }
}

/* 🔴 CUANDO LA SESIÓN YA NO VALE, HAY QUE VOLVER A ENTRAR — no quedarse
   mirando un «SIN ACCESO» que no lleva a ninguna parte. Aquí se avisa una vez y
   la puerta de login se encarga. Se distingue a propósito de un 403: un 401 es
   «no sé quién eres», que se arregla entrando; un 403 es «sé quién eres y
   Hotelos no te deja», que se arregla pidiéndoselo a Sistemas. */
let avisarPerdida: (() => void) | null = null;
export const alPerderSesion = (fn: (() => void) | null) => { avisarPerdida = fn; };
let yaAvisado = false;

const esDenegado = (e: unknown) => {
  const s = (e as { status?: number })?.status;
  return s === 401 || s === 403;
};

/* 🔴 Un «no tienes permiso» NUNCA debe caer al demo: enseñaría cifras
   inventadas a quien precisamente no debe ver las de verdad, y encima con
   pinta de reales. Se distingue del fallo de red (que sí merece demo) por el
   código: 401/403 devuelven el valor vacío que pase quien llama. */
async function conFallback<T>(path: string, demo: () => T, vacio?: () => T): Promise<Res<T>> {
  if (MODE === 'mock') return { data: demo(), source: 'demo' };
  try {
    return { data: await real<T>(path), source: 'live' };
  } catch (e1) {
    if (esDenegado(e1)) return { data: (vacio ?? demo)(), source: 'denegado' };
    if (MODE !== 'real') {
      // un reintento antes de caer al demo: que un parpadeo de red no degrade
      try {
        return { data: await real<T>(path), source: 'live' };
      } catch (e2) {
        if (esDenegado(e2)) return { data: (vacio ?? demo)(), source: 'denegado' };
        return { data: demo(), source: 'demo' };
      }
    }
    throw new Error(`BFF ${path} inaccesible.`);
  }
}

export interface PdvPunto {
  punto: string; sinIva: number; conIva: number; lineas: number;
  /** Acumulado del mes de ESE punto, del día 1 al día pedido. Puede haber `mes`
      con `sinIva` a 0: un punto que facturó el martes y hoy está cerrado. */
  mes?: number;
  /** Días del mes en los que ese punto ha facturado algo. */
  diasMes?: number;
}
export interface HotelDiaLive {
  hotel: string; id: number; fecha: string; esHoy: boolean; hayDatos: boolean;
  ultimoConDatos: string | null; ultSync: string | null;
  totalSin: number; totalCon: number; lineas: number;
  recepcion: PdvPunto[]; pos: PdvPunto[];
  subRecepcion: { sinIva: number; mes?: number; lineas: number };
  subPos: { sinIva: number; mes?: number; lineas: number };
  /** ¿Este hotel publica puntos de venta? `noray_facturacion` es de Noray: un
      hotel de OnaSystem, Navision o Avalon no los tiene NUNCA, que es distinto
      de tenerlos y estar a cero. Se dice, no se deja un hueco. */
  publicaPuntos?: boolean;
  /** Primer día del mes acumulado (día 1 del mes de `fecha`). */
  mesDesde?: string;
  nota: string;
}
export interface ProdGrupo { cuenta: string; nombre: string; uds: number; neto: number; bruto: number; lineas: Array<{ concepto: string; uds: number; neto: number }> }
export interface ProduccionDiaLive {
  hotel: string; id: number; fecha: string; hayDatos: boolean;
  ultimoConDatos: string | null; total: number; totalBruto: number; uds: number;
  grupos: ProdGrupo[]; nota: string;
}

export interface CobrosVentana {
  dias?: number; hasta: string; clientes: number; total: number; neto: number;
  top: Array<{ n: string; imp: number; vence: string | null }>;
}
export interface AgeingTramo { clave: string; texto: string; vencido: boolean; importe: number; top: Array<{ n: string; imp: number }> }
export interface AgeingLive {
  corte: string; total: number; vencido: number; noVencido: number; facturas: number;
  tramos: AgeingTramo[];
  clientes: Array<{ n: string; cod: string; total: number; vencido: number; masAntigua: string | null }>;
  /** Lo que vence en los PRÓXIMOS 30 días: dinero que va a entrar, con nombre y
      fecha. El ageing normal mira hacia atrás; esto mira hacia delante.
      `total` suma los clientes en positivo y cuadra con `top`; `neto` incluye
      los anticipos (que llegan en negativo). Si no coinciden, hay cobros a
      cuenta en el tramo y la tarjeta lo dice. */
  prox30?: CobrosVentana;
  /** Las tres ventanas que pidió Patricio para Noa: 30, 60 y 90 días. Son
      ACUMULADAS, no tramos: la de 60 incluye los primeros 30. */
  cobros?: CobrosVentana[];
  nota: string;
}
export interface CajaLive {
  fecha: string; importe: number; apuntes: number; mes: number;
  serie: number[]; dias: string[]; nota: string;
}

export interface NoaEnsenanza { id: string; pregunta: string; respuesta: string; pie: string | null; quien: string | null; fecha: string }

export type Vivo<T> = { ok: true; dato: T } | { ok: false; motivo: string; status?: number };

/* Trae un bloque que solo existe en vivo, con el MOTIVO cuando no se puede. */
async function vivo<T>(path: string, cuerpo?: unknown): Promise<Vivo<T>> {
  if ((MODE as string) === 'mock') return { ok: false, motivo: 'Vista previa sin conexión al servidor.' };
  try {
    return { ok: true, dato: await real<T>(path, cuerpo) };
  } catch (e) {
    const err = e as Error & { status?: number };
    const status = err.status;
    const motivo = status === 403 ? (err.message || 'Tu perfil de Ona Pulse no incluye esta información.')
      : status === 401 ? 'Hay que volver a iniciar sesión.'
      : status && status >= 500 ? (err.message || 'El servidor no ha podido calcularlo ahora mismo.')
      : 'No se ha podido contactar con el servidor.';
    return { ok: false, motivo, status };
  }
}

/* ── Estado de habitaciones · la foto de gobernanta del PMS ────────────────
   🔴 `grupo` NO cuenta los hoteles sin ciclo diario de limpieza. Son
   apart-hoteles de socios —solo se limpia al entrar y salir un socio—, así que
   sus habitaciones «sin hacer» no son suciedad: la pregunta no aplica. Van en
   `sinCiclo`, contadas y dichas, nunca repartidas dentro del porcentaje. */
export interface HabHotel {
  id: number; hotel: string; habs: number;
  sinHacer: number; enLimpieza: number; limpias: number; revisadas: number;
  repasar: number; averiadas: number; otras: number;
  listas: number; pendientes: number; bloqueadas: number;
  vendidas: number | null;
  /** COTA INFERIOR: el espejo dice cuántas están vendidas, no cuáles. */
  vendidasSinHacer: number | null;
  sinCiclo: boolean; foto: string | null;
  /** El motivo que más pesa en ESE hotel: un total de 258 no dice a quién llamar. */
  motivoBloqueo: { motivo: string; n: number } | null;
}
export interface HabEstadoLive {
  /** La foto MÁS VIEJA de todos los hoteles: el grupo es tan viejo como el que va último. */
  foto: string | null;
  fotoNueva: string | null;
  grupo: {
    habs: number; listas: number; pendientes: number; enLimpieza: number;
    otras: number; vendidas: number; vendidasSinHacer: number; hoteles: number;
  };
  sinCiclo: { hoteles: Array<{ hotel: string; habs: number; vendidas: number | null }>; habs: number };
  bloqueos: {
    vigentes: number; lineas: number; abiertos: number;
    motivos: Array<{ motivo: string; n: number }>;
    /** El otro número, el del planning. Se declara porque NO coincide. */
    planning: number | null;
  };
  hoteles: HabHotel[];
}

/* ── Sistemas / Revenue: bloques que solo existen EN VIVO ──────────────────
   Devuelven null si el servidor no los da. Nunca hay versión demo de estos:
   una salud de sistemas inventada es peor que no enseñar ninguna. */
export interface SyncFallo {
  tipo: string; que: string; ok?: boolean; cuando: string | null;
  /** true = `cuando` es la hora en que se tocó el ESPEJO (la de verdad).
      false = es la del último parte, que solo se escribe cuando algo falla. */
  esFrescura?: boolean;
  /** Este sync corre cada pocos minutos y solo deja constancia si falla. */
  silencioso?: boolean;
  /** Cuándo fue la última incidencia, si la hubo. */
  incidencia?: string | null;
  origen: string | null; error: string | null; nuevos?: number; actualizados?: number;
}
export interface PmsLatido {
  pms: string; estado: 'ok' | 'warn' | 'bad'; hoteles: number; msg: string;
  ultima?: string | null;
  /* El detalle de cada sync de ese PMS: sin esto la tarjeta decía «2 fallos» y
     había que ir a Hotelos a averiguar cuáles. */
  detalle?: SyncFallo[];
  silencioso?: boolean;
}
export interface SistemasLive {
  pms: PmsLatido[];
  syncs: { verde: number; fallos: number; fallosTipos: string[]; fallosDetalle?: SyncFallo[]; total: number; ventana: string };
  cola: { hoteles: number; rojos: number; enError: number; pendientes: number; revisado: string | null } | null;
  sinMapear: string[];
  nota: string;
}
export interface RejillaFila { id: number; n: string; pms: string; celdas: Array<number | null>; ob: number }
export interface RejillaLive { fechas: string[]; filas: RejillaFila[]; umbralOverbooking: number; desde?: number }
export interface PickupPms { pms: string; rn: number | null; reservas: number | null; cubierto: boolean; sinFecha?: number; error?: string }
export interface PickupLive {
  disponible: boolean;
  rn: number | null; reservas: number | null;
  serie: number[]; dias: string[];
  mejorDia: { fecha: string; rn: number } | null;
  porPms: PickupPms[]; faltan: string[];
  ventana: { desde: string; hasta: string; llegadasHasta: string };
  nota: string;
}

export const api = {
  /* Versión del SERVIDOR (sin token): para el pie de la app. null = inalcanzable. */
  async serverVersion(): Promise<string | null> {
    try {
      const res = await fetch(`${BASE_URL}/health`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const j = (await res.json()) as { version?: string };
      return j.version ?? null;
    } catch {
      return null;
    }
  },

  brief: (): Promise<Res<Brief>> =>
    conFallback<Brief>('/brief', () => ({
      prodDia: G.prodDia, prodMes: G.prodMes,
      // Sin budget INVENTADO ni en demo: el anillo de 104% con cifras falsas
      // al lado de datos reales confundía (pasó). Cuando haya budget real, vendrá del BFF.
      budMes: null, budPct: null, deltaDia: null,
      occ: G.occ, hoteles: HOTELS.length, pms: 4,
      // reversos del carrusel en modo demo (proporciones plausibles, marcadas DEMO por la cabecera)
      fuentes: [
        { pms: 'noray', ok: true, hoteles: 12, dia: 361480, mes: 7290000 },
        { pms: 'onasystem', ok: true, hoteles: 2, dia: 38755, mes: 790000 },
        { pms: 'navision', ok: true, hoteles: 2, dia: 48000, mes: 1020000 },
        { pms: 'avalon', ok: true, hoteles: 1, dia: 39085, mes: 740000 },
      ],
      occPorPms: [
        { pms: 'Noray', media: 89.2, hoteles: 12 },
        { pms: 'Onasystem', media: 82.5, hoteles: 2 },
        { pms: 'Navision', media: 80.5, hoteles: 2 },
        { pms: 'Avalon', media: 92.8, hoteles: 1 },
      ],
      adr: null, adrPorPms: [],
    })),

  hoteles: (): Promise<Res<HotelRow[]>> =>
    conFallback<HotelRow[]>('/hoteles', () =>
      [...HOTELS].sort((a, b) => b.mes - a.mes).map((h) => ({ ...h }))),

  alertas: (): Promise<Res<Alerta[]>> =>
    conFallback<Alerta[]>('/alertas', () => ALERTS, () => []),

  premio: (): Promise<Res<Premio>> =>
    /* 🔴 En demo el podio se declara PENDIENTE, no se inventan tres ganadores.
       Un premio con nombres falsos es la clase de dato que alguien repite en
       una reunión. */
    conFallback<Premio>('/premio', () => ({
      disponible: false, pendiente: true,
      motivo: 'Vista previa: el podio real sale de la edición publicada en Hotelos › Premios Noa.',
    }), () => ({ disponible: false, motivo: 'Tu perfil de Ona Pulse no incluye el resultado financiero.' })),

  /* Quién soy según el API: identidad real y qué capacidades resuelve Hotelos.
     null = no se pudo preguntar (sin red, o entorno sin login). */
  yo: async (): Promise<{ identificado: boolean; legado?: boolean; modo?: string; nombre?: string;
                          rol?: string | null;
                          /** TODOS sus roles (migr 175 de Hotelos). `rol` es el principal:
                              el que decide qué portada se abre. Los demás son suyos igual. */
                          roles?: string[];
                          capacidades?: string[]; esAdmin?: boolean;
                          /** 401 = la sesión ya no vale (hay que volver a entrar).
                              403 = te identificamos, pero Hotelos no te da acceso. */
                          status?: number; motivo?: string } | null> => {
    try { return await real('/yo'); } catch (e) {
      const err = e as Error & { status?: number };
      if (esDenegado(e)) {
        return { identificado: false, capacidades: [], status: err.status, motivo: err.message };
      }
      return null;
    }
  },

  /* Reparto de la app: qué hay disponible y el vale de un solo uso. */
  descargaEstado: async (): Promise<{ android: boolean; bytes?: number; fecha?: string } | null> => {
    try { return await real('/descarga/estado'); } catch { return null; }
  },
  descargaVale: async (): Promise<{ vale: string; segundos: number } | null> => {
    try { return await real('/descarga/vale'); } catch { return null; }
  },

  /* Folios vivos de hoy y puntos de venta — solo LIVE (en demo devuelven null). */
  hoy: async (): Promise<HoyLive | null> => {
    if ((MODE as string) === 'mock') return null;
    try { return await real<HoyLive>('/hoy'); } catch { return null; }
  },
  /* Sistemas, ocupación 30 días y pickup: LIVE o nada (ver comentario arriba).
     🔴 El error se DEVUELVE, no se traga: «tu perfil no incluye esto» (403),
     «el servidor no responde» y «ese espejo está caído» son tres cosas
     distintas y la tarjeta tiene que poder decir cuál es. Antes las tres
     acababan en el mismo null y la app culpaba siempre a los espejos. */
  sistemas: () => vivo<SistemasLive>('/sistemas'),
  ageing: () => vivo<AgeingLive>('/ageing'),
  comparaAnio: (hotel: number, mes?: number) =>
    vivo<ComparaAnioLive>(`/compara-anio?hotel=${hotel}${mes ? `&mes=${mes}` : ''}`),
  fichaHotel: (hotel: number, fecha?: string) =>
    vivo<FichaHotelLive>(`/ficha-hotel?hotel=${hotel}${fecha ? `&fecha=${fecha}` : ''}`),

  /* ── Enseñarle a Noa ─────────────────────────────────────────────────
     `noaRespuesta` la consulta la propia Noa antes de rendirse. Las otras
     tres son para la pantalla de repaso: exigen la capacidad de Sistemas y el
     servidor lo vuelve a comprobar — el botón se esconde por educación, no
     por seguridad. */
  noaRespuesta: async (q: string): Promise<{ id: string; txt: string; pie: string | null } | null> => {
    if ((MODE as string) === 'mock') return null;
    try {
      const r = await real<{ vacio?: boolean; id: string; txt: string; pie: string | null }>(
        `/noa/respuesta?q=${encodeURIComponent(q)}`);
      return r && !r.vacio ? r : null;
    } catch { return null; }
  },
  noaEnsena: (pregunta: string, respuesta: string, pie?: string) =>
    vivo<{ ok: boolean; id: string }>(`/noa/ensena`, { pregunta, respuesta, pie }),
  noaOlvida: (id: string) => vivo<{ ok: boolean }>(`/noa/olvida`, { id }),
  noaEnsenanzas: () => vivo<{ total: number; lista: NoaEnsenanza[] }>('/noa/ensenanzas'),
  noaPreguntas: (soloFallos = true) =>
    vivo<{ total: number; sinRespuesta: number; grupos: Array<{ ejemplo: string; veces: number; sinRespuesta: number; personas: number }> }>(
      `/noa/preguntas?fallos=${soloFallos ? 1 : 0}`),
  /* Marcar leído. Va a `notificaciones_vistas` de Hotelos: leer en la web lo
     marca en el móvil y al revés. El reparto ya NO se configura aquí. */
  avisosLeer: (claves: string[]) => vivo<{ marcados: number; error?: string }>('/avisos/leer', { claves }),
  comercial: () => vivo<ComercialLive>('/comercial'),
  sistemasKpis: () => vivo<SistemasKpis>('/sistemas-kpis'),
  assetKpis: () => vivo<AssetKpisLive>('/asset-kpis'),
  comisiones: () => vivo<ComisionesLive>('/comisiones'),
  habitacionesEstado: () => vivo<HabEstadoLive>('/habitaciones-estado'),
  caja: () => vivo<CajaLive>('/caja'),
  /* Un hotel y un día: el desglose por punto de venta y el de producción por
     cuenta. Son los dos informes que dirección mira a diario en Hotelos. */
  hotelDia: (hotel: number, fecha?: string) =>
    vivo<HotelDiaLive>(`/hotel-dia?hotel=${hotel}${fecha ? `&fecha=${fecha}` : ''}`),
  produccionDia: (hotel: number, fecha?: string) =>
    vivo<ProduccionDiaLive>(`/produccion-dia?hotel=${hotel}${fecha ? `&fecha=${fecha}` : ''}`),

  /* El cuaderno de Noa: se apunta lo que le preguntan para saber qué enseñarle.
     A propósito no se espera respuesta ni se avisa de un fallo — apuntar nunca
     puede entorpecer la conversación. */
  noaApunta: (pregunta: string, intencion: string | null, resuelta: boolean) => {
    cabeceras().then((headers) => fetch(`${BASE_URL}/noa/pregunta`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pregunta, intencion, resuelta }),
    })).catch(() => { /* da igual */ });
  },
  /* `desde` en negativo arranca antes de hoy: la vista de «ayer, hoy y los 7
     próximos» necesita el día pasado. */
  rejilla: (dias = 30, desde = 0) =>
    vivo<RejillaLive>(`/rejilla?dias=${dias}${desde ? `&desde=${desde}` : ''}`),
  pickup: () => vivo<PickupLive>('/pickup'),

  pdv: async (q: string, hotel?: number | null): Promise<PdvLive | null> => {
    if ((MODE as string) === 'mock') return null;
    const url = `/pdv?q=${encodeURIComponent(q)}${hotel != null ? `&hotel=${hotel}` : ''}`;
    try { return await real<PdvLive>(url); } catch { return null; }
  },

  // Ona IA queda en demo hasta la fase 2 (tool-use de solo lectura en el BFF).
  async ia(pregunta: string): Promise<IaReply> {
    await delay(900);
    const q = pregunta.toLowerCase();
    const hit = IA_ANSWERS.find((a) => a.k.some((k) => q.includes(k)));
    if (hit) return { txt: hit.txt, rows: hit.rows, foot: hit.foot };
    return {
      txt: 'En la beta respondo a unas pocas preguntas. Conectado al BFF, esta pestaña consulta Hotelos en vivo (producción, budget, ageing, ocupación…) y responde con cifras verificables.',
      rows: [],
      foot: '',
    };
  },
};
